// A work session's channel/thread notice: the card the relay posts, or (rows posted before the
// card existed) the same notice parsed back out of its text. Undefined: not a notice.
import type { Message, WorkSessionNoticeCard } from "$lib/core/types.js";
import { reportKpi, shouldReport } from "$lib/report-failure.js";

export type WorkSessionNoticeStatus = WorkSessionNoticeCard["status"];

export interface WorkSessionNoticeView {
  workSessionId: string;
  status: WorkSessionNoticeStatus;
  cyboId: string | null;
  // The workspace the deep link names; null: the message's own workspace.
  workspaceId: string | null;
  at: number;
  legacy: boolean;
}

// The relay's copy (work-session-notify.ts workSessionNotice) + "Open the session: <url>".
const LEGACY_RE =
  /^.* (finished the work|needs your approval to continue|stopped before finishing your session)\. Open the session: \S*\/workspace\/([^/\s]+)\/session\/([^/\s?#]+)\s*$/;
const LEGACY_STATUS: Record<string, WorkSessionNoticeStatus> = {
  "finished the work": "done",
  "needs your approval to continue": "waiting_approval",
  "stopped before finishing your session": "paused",
};

export function workSessionNoticeOf(
  message: Pick<Message, "fromType" | "card" | "text" | "source" | "createdAt">,
): WorkSessionNoticeView | undefined {
  if (message.fromType !== "system") return undefined;
  const card = message.card;
  if (card?.kind === "work_session_notice") {
    return {
      workSessionId: card.workSessionId,
      status: card.status,
      cyboId: card.cyboId,
      workspaceId: null,
      at: card.at || message.createdAt,
      legacy: false,
    };
  }
  if (card || message.source !== "work_session") return undefined;
  const m = LEGACY_RE.exec(message.text ?? "");
  if (!m) return undefined;
  return {
    workSessionId: m[3],
    status: LEGACY_STATUS[m[1]],
    cyboId: null,
    workspaceId: m[2],
    at: message.createdAt,
    legacy: true,
  };
}

type NoticeRow = Pick<Message, "id" | "fromType" | "card" | "text" | "source" | "createdAt">;

// The relay keeps ONE notice row per session, rewritten in place (O4). Rows posted before that
// (one per turn) are hidden behind each session's newest row: the ids returned here.
export function supersededWorkSessionNotices(messages: readonly NoticeRow[]): ReadonlySet<string> {
  const newest = new Map<string, NoticeRow>();
  const superseded = new Set<string>();
  for (const message of messages) {
    const view = workSessionNoticeOf(message);
    if (!view) continue;
    const kept = newest.get(view.workSessionId);
    if (kept && kept.createdAt > message.createdAt) {
      superseded.add(message.id);
      continue;
    }
    if (kept) superseded.add(kept.id);
    newest.set(view.workSessionId, message);
  }
  return superseded;
}

/** The nearest row before `index` (not below `floor`) that renders: a collapsed notice is skipped. */
export function previousShown<T extends { id: string }>(
  rows: readonly T[],
  index: number,
  hidden: ReadonlySet<string>,
  floor = 0,
): T | null {
  for (let i = index - 1; i >= floor; i--) {
    if (!hidden.has(rows[i].id)) return rows[i];
  }
  return null; // null: nothing renders above this row (in the mounted window)
}

/** Counts the hidden legacy rows, so the collapse can be retired once they stop showing up. */
export function reportCollapsedNotices(
  hidden: number,
  from: "channel" | "thread" | "feed",
  workspaceId: string | undefined,
): void {
  if (hidden === 0 || !workspaceId) return;
  if (shouldReport(`work_session_notice.collapsed:${from}:${workspaceId}`, 3_600_000)) {
    reportKpi("work_session_notice.collapsed", { hidden, from, workspaceId });
  }
}
